import type { SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import { z } from 'zod';

const salesAddressSchema = z.object({
    address_id: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    country_code: z.string().optional(),
    line1: z.string().optional(),
    line2: z.string().optional(),
    line3: z.string().optional(),
    zip: z.string().optional(),
});

export type SalesAddress = z.infer<typeof salesAddressSchema>;

const salesDraftTypeSchema = z.enum(['ORDER', 'QUOTE']);

export const createSalesDraftInputSchema = z.object({
    draft_order_id: z.string(),
    type: salesDraftTypeSchema,
    customer_id: z.string(),
    billing_address: salesAddressSchema.optional(),
    shipping_address: salesAddressSchema.optional(),
    contact_person_id: z.string().optional().default(''),
    contact_person_name: z.string().optional().default(''),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    storage_id: z.string(),
    notes: z.string().optional().default(''),
    external: z.record(z.string(), z.any()).optional(),
    created_by: z.string().optional(),
});

export const createNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    product_variant_id: z.string(),
    storage_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string().nullable(),
    shipping_type_id: z.string(),
    sales_units_per_package_unit: z.number().optional(),
    partial_packing_type: z.string().optional(),
    price_scale_factor: z.number().optional(),
});

export type CreateNormalLineItemInput = z.infer<typeof createNormalLineItemInputSchema>;

export const salesDraftProductVariantConfigInputSchema = z.object({
    productVariantId: z.string(),
});

export type SalesDraftType = z.infer<typeof salesDraftTypeSchema>;

export const salesDiscountSchema = z.object({
    code: z.string(),
    amount: z.number(),
    percent: z.number(),
});

export const updateNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
    product_variant_id: z.string(),
    storage_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string(),
    price: z.number(),
    rebate: z.array(salesDiscountSchema),
    shipping_type_id: z.string(),
    note: z.string(),
    external: z.record(z.string(), z.any()),
});

export type UpdateNormalLineItemInput = z.infer<typeof updateNormalLineItemInputSchema>;

export const deleteNormalLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
});

export type DeleteNormalLineItemInput = z.infer<typeof deleteNormalLineItemInputSchema>;

export const salesDraftDefaultHeaderInputSchema = z.object({
    sales_order_type: salesDraftTypeSchema,
    customer_id: z.string(),
    clerk_id: z.string(),
});

export type SalesDraftDefaultHeaderInput = z.infer<typeof salesDraftDefaultHeaderInputSchema>;

export type SalesDraftDefaultHeaderOutput = z.infer<typeof salesDraftDefaultHeaderOutputSchema>;

export const salesDraftHeaderSchema = z.object({
    draft_order_id: z.string().min(1, 'Required'),
    type: salesDraftTypeSchema,
    shipping_address: salesAddressSchema,
    billing_address: salesAddressSchema,
    contact_person_id: z.string().optional().default(''),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    notes: z.string().optional().default(''),
    external: z.object({
        document_type: z.string().min(1, 'Required'),
        po_number: z.string().optional().default(''),
    }),
});

export const updateSalesDraftInputSchema = z.object({
    draft_order_id: z.string().min(1, 'Required'),
    type: salesDraftTypeSchema,
    shipping_address: salesAddressSchema.optional(),
    billing_address: salesAddressSchema,
    contact_person_id: z.string().optional().default(''),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    notes: z.string().optional().default(''),
    external: z.object({
        document_type: z.string().min(1, 'Required'),
        po_number: z.string().optional().default(''),
    }),
    created_by: z.string().optional(),
    contact_person_name: z.string().optional(),
    storage_id: z.string().optional(),
    release: z.boolean().optional(),
});

export type UpdateSalesDraftInput = z.input<typeof updateSalesDraftInputSchema>;

export const submitSalesDraftInputSchema = z.object({
    draft_order_id: z.string(),
    clerk_id: z.string(),
});

export type SubmitSalesDraftInput = z.infer<typeof submitSalesDraftInputSchema>;

export const salesDraftPriceInputSchema = z.object({
    product_variant_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string(),
    customer_id: z.string(),
    storage_id: z.string(),
    rebate: z.array(salesDiscountSchema),
    price: z.number().optional(),
});

export const salesDraftSalesConfigInputSchema = z.object({
    product_variant_id: z.string(),
});

const partialPackingTypeSchema = z.string() as z.ZodType<SrPartialPackingTypeMap>;

export const salesDraftSalesConfigOutputSchema = z.object({
    factor_sales_unit: z.number().optional(),
    package_unit: z.string().optional(),
    partial_packing_type: partialPackingTypeSchema.optional(),
    price_scale_factor: z.number().optional(),
    product_variant_id: z.string(),
    quantity_unit_sales: z.string().optional(),
    quantity_unit_storage: z.string().optional(),
    sales_units_per_package_unit: z.number().optional(),
});

export type SalesDraftPriceInput = z.infer<typeof salesDraftPriceInputSchema>;

export type SalesDraftPriceOutput = z.infer<typeof salesDraftPriceOutputSchema>;

export type SalesDraftSalesConfigOutput = z.infer<typeof salesDraftSalesConfigOutputSchema>;

export const addNoteLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    note: z.string().min(1),
    external: z.record(z.string(), z.any()),
});

export type AddNoteLineItemInput = z.infer<typeof addNoteLineItemInputSchema>;

export const updateNoteLineItemInputSchema = z.object({
    draft_order_id: z.string(),
    draft_line_item_id: z.string(),
    note: z.string(),
    external: z.record(z.string(), z.any()),
});

export type UpdateNoteLineItemInput = z.infer<typeof updateNoteLineItemInputSchema>;

export const deleteNoteLineItemInputSchema = z.object({
    draft_line_item_id: z.string(),
    draft_order_id: z.string(),
});

export type DeleteNoteLineItemInput = z.infer<typeof deleteNoteLineItemInputSchema>;

export type SalesDraftHeaderExtraRequiredField = 'po_number' | 'contact_person_id';

export type DraftSalesOrderLineItem = PrismaJson.DraftSalesOrderLineItem[number];

export const salesDraftStateSchema = z.enum(['NEW', 'SUBMITTED']);
export type SalesDraftState = z.infer<typeof salesDraftStateSchema>;

export const salesDraftSortFieldSchema = z.enum(['CREATED_BY', 'CREATED_AT']);
export type SalesDraftSortField = z.infer<typeof salesDraftSortFieldSchema>;

export const salesDraftSortDirectionSchema = z.enum(['ASC', 'DESC']);
export type SalesDraftSortDirection = z.infer<typeof salesDraftSortDirectionSchema>;

export const salesDraftSortField = salesDraftSortFieldSchema.enum;

export type SalesDraftSort = {
    field: SalesDraftSortField;
    direction: SalesDraftSortDirection;
};

export type SalesDraftFilterRequest = {
    customers: string[];
    users: string[];
    search_query?: string;
    states?: SalesDraftState[];
};

const salesDraftCreatorSchema = z.object({
    user_id: z.string(),
    email: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    fullname: z.string(),
});

const salesDraftOrderBaseSchema = z.object({
    draft_order_id: z.string(),
    customer_id: z.string(),
    customer_name: z.string(),
    storage_name: z.string(),
    sales_order_id: z.string().optional(),
    state: salesDraftStateSchema,
    type: salesDraftTypeSchema.default('ORDER'),
    clerk_id: z.string().optional(),
    billing_address: salesAddressSchema.optional(),
    shipping_address: salesAddressSchema.optional(),
    contact_person_id: z.string().optional(),
    contact_person_name: z.string().optional(),
    due_date: z.string().optional(),
    delivery_date: z.string().optional(),
    shipping_type_id: z.string().optional(),
    storage_id: z.string().optional(),
    notes: z.string().optional(),
    total_amount: z.number(),
    total_gross_margin: z.number(),
    release: z.boolean().optional(),
    external: z.record(z.string(), z.any()).optional(),
    creator: salesDraftCreatorSchema,
    submitted_at: z.string().optional(),
    created_at: z.string().optional(),
});

const salesDraftOrderListItemSchema = salesDraftOrderBaseSchema.extend({
    line_items: z.array(
        z.object({
            line_item_id: z.string(),
            type: z.string(),
            normal_line_item: z
                .object({
                    product_variant_id: z.string(),
                    quantity: z.number(),
                    quantity_unit: z.string(),
                    price: z.number(),
                    total: z.number(),
                    gross_margin: z.number(),
                    rebate: z.array(salesDiscountSchema),
                    storage_id: z.string(),
                    shipping_type_id: z.string(),
                    note: z.string().optional(),
                })
                .optional(),
            note_line_item: z
                .object({
                    note: z.string(),
                })
                .optional(),
            external: z.record(z.string(), z.any()),
        }),
    ),
});

export type SalesDraftOrderListItem = z.infer<typeof salesDraftOrderListItemSchema>;

export const salesDraftListOutputSchema = z.object({
    items: z.array(salesDraftOrderListItemSchema),
    nextCursor: z.string().optional(),
});

const filterGroupSchema = z.object({
    options: z.array(
        z.object({
            option_id: z.string(),
            option: z.string(),
            drafts_quantity: z.number(),
        }),
    ),
    totals: z.object({ total: z.number() }),
});

export type SalesDraftFilterGroup = z.infer<typeof filterGroupSchema>;

export const salesDraftFilterOptionsSchema = z.object({
    customers: filterGroupSchema,
    users: filterGroupSchema,
    states: filterGroupSchema,
});

const salesDraftNormalLineItemSchema = z.object({
    product_variant_id: z.string(),
    quantity: z.number(),
    quantity_unit: z.string(),
    price: z.number(),
    rebate: z.array(salesDiscountSchema).optional(),
    gross_margin: z.number(),
    total: z.number(),
    storage_id: z.string(),
    shipping_type_id: z.string(),
    note: z.string().optional(),
    line_item_id: z.string(),
    storage_name: z.string(),
    external: z.record(z.string(), z.any()),
    product_variant_name: z.string().optional(),
    sales_units_per_package_unit: z.number().optional(),
    partial_packing_type: partialPackingTypeSchema.optional(),
    price_scale_factor: z.number().optional(),
});

export type SalesDraftNormalLineItem = z.infer<typeof salesDraftNormalLineItemSchema>;

const salesDraftNoteLineItemSchema = z.object({
    note: z.string(),
    line_item_id: z.string(),
    external: z.record(z.string(), z.any()),
});

export type SalesDraftNoteLineItem = z.infer<typeof salesDraftNoteLineItemSchema>;

const salesDraftLineItemSchema = z.object({
    line_item_id: z.string(),
    type: z.string(),
    normal_line_item: salesDraftNormalLineItemSchema.optional(),
    note_line_item: salesDraftNoteLineItemSchema.optional(),
    external: z.record(z.string(), z.any()),
});

export type SalesDraftLineItem = z.infer<typeof salesDraftLineItemSchema>;

const salesDraftOrderSchema = salesDraftOrderBaseSchema.extend({
    line_items: z.array(salesDraftLineItemSchema),
});

export type SalesDraftOrder = z.infer<typeof salesDraftOrderSchema>;

export const salesDraftDetailOutputSchema = z.object({
    draft_order: salesDraftOrderSchema,
});

export type SalesDraftDetailOutput = z.infer<typeof salesDraftDetailOutputSchema>;

export const salesDraftDefaultHeaderOutputSchema = z.object({
    invoice_address: salesAddressSchema,
    shipping_address: salesAddressSchema,
    storage_id: z.string(),
    shipping_type: z.string(),
    sales_order_type: z.string(),
    due_date: z.string(),
    desired_delivery_date: z.string(),
});

export const salesDraftPriceOutputSchema = z.object({
    price: z.object({
        article_id: z.string(),
        quantity: z.number(),
        price: z.number(),
        margin: z.number(),
        total: z.number(),
        rebate: z.array(salesDiscountSchema),
        customer_id: z.string(),
        storage_id: z.string(),
        quantity_unit: z.string().optional(),
        external: z.record(z.string(), z.any()),
    }),
});

export const salesDraftProductVariantConfigOutputSchema = z.object({
    stock: z.array(
        z.object({
            storage_id: z.string(),
            available: z.number().optional(),
            effective: z.number().optional(),
            picked: z.number().optional(),
            reserved: z.number().optional(),
            stock_quantity: z.number().optional(),
            storage_name: z.string(),
            quantity_unit_sales: z.string().optional(),
            product_variant_name: z.string().optional(),
            product_variant_id: z.string().optional(),
            sales_units_per_package_unit: z.number().optional(),
            partial_packing_type: z.string().optional(),
            parts_list_type: z.string().optional(),
        }),
    ),
});

export type SalesDraftProductVariantConfigOutput = z.infer<typeof salesDraftProductVariantConfigOutputSchema>;
